import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff, BarChart3, Code, ExternalLink, Copy, CheckCircle2, AlertCircle, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import apiClient from '../../lib/api-client';
import { PaginationWrapper } from '../ui/PaginationWrapper';

interface WordPressAd {
  _id: string;
  title: string;
  size: string;
  dimensions: {
    width: string;
    height: string;
  };
  imageUrl: string;
  linkUrl: string;
  targetBlank: boolean;
  isActive: boolean;
  position: number;
  displayLocations: string[];
  customCSS: string;
  trackClicks: boolean;
  clickCount: number;
  impressionCount: number;
  createdAt: string;
  updatedAt: string;
}

interface AdStats {
  totalAds: number;
  activeAds: number;
  totalClicks: number;
  totalImpressions: number;
}

export default function AdminWordPressAdsEnhanced() {
  const [ads, setAds] = useState<WordPressAd[]>([]);
  const [stats, setStats] = useState<AdStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAd, setEditingAd] = useState<WordPressAd | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [previewAd, setPreviewAd] = useState<WordPressAd | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    size: 'banner',
    width: '100%',
    height: 'auto',
    imageUrl: '',
    linkUrl: '',
    targetBlank: true,
    isActive: true,
    position: 0,
    displayLocations: ['all'] as string[],
    customCSS: '',
    trackClicks: true
  });

  useEffect(() => {
    fetchAds();
    fetchStats();
  }, []);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/wordpress-ads/all');
      if (response.data.success) {
        setAds(response.data.ads);
        if (response.data.ads.length > 0 && !previewAd) {
          setPreviewAd(response.data.ads[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast.error('Failed to fetch ads');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/wordpress-ads/statistics');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...formData,
        dimensions: {
          width: formData.width,
          height: formData.height
        }
      };

      if (editingAd) {
        const response = await apiClient.put(`/wordpress-ads/${editingAd._id}`, payload);
        if (response.data.success) {
          toast.success('Ad updated successfully');
          setAds(ads.map(ad => ad._id === editingAd._id ? response.data.ad : ad));
        }
      } else {
        const response = await apiClient.post('/wordpress-ads', payload);
        if (response.data.success) {
          toast.success('Ad created successfully');
          setAds([...ads, response.data.ad]);
          if (!previewAd) {
            setPreviewAd(response.data.ad);
          }
        }
      }
      
      setShowDialog(false);
      resetForm();
      fetchStats();
    } catch (error: any) {
      console.error('Error saving ad:', error);
      toast.error(error.response?.data?.message || 'Failed to save ad');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ad: WordPressAd) => {
    setEditingAd(ad);
    setFormData({
      title: ad.title,
      size: ad.size,
      width: ad.dimensions.width,
      height: ad.dimensions.height,
      imageUrl: ad.imageUrl,
      linkUrl: ad.linkUrl,
      targetBlank: ad.targetBlank,
      isActive: ad.isActive,
      position: ad.position,
      displayLocations: ad.displayLocations,
      customCSS: ad.customCSS,
      trackClicks: ad.trackClicks
    });
    setShowDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this ad?')) return;
    
    try {
      setLoading(true);
      const response = await apiClient.delete(`/wordpress-ads/${id}`);
      if (response.data.success) {
        toast.success('Ad deleted successfully');
        setAds(ads.filter(ad => ad._id !== id));
        if (previewAd?._id === id && ads.length > 1) {
          setPreviewAd(ads.find(ad => ad._id !== id) || null);
        }
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting ad:', error);
      toast.error('Failed to delete ad');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      const response = await apiClient.patch(`/wordpress-ads/${id}/toggle`);
      if (response.data.success) {
        toast.success('Ad status updated');
        setAds(ads.map(ad => ad._id === id ? response.data.ad : ad));
        fetchStats();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      size: 'banner',
      width: '100%',
      height: 'auto',
      imageUrl: '',
      linkUrl: '',
      targetBlank: true,
      isActive: true,
      position: 0,
      displayLocations: ['all'],
      customCSS: '',
      trackClicks: true
    });
    setEditingAd(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setShowDialog(true);
  };

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(type);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const apiBaseUrl = window.location.origin.replace(/:\d+/, ':5001') + '/api';

  const shortcodeExample = `[matrimonial_banner size="banner"]`;
  const widgetCode = `
// In your WordPress theme or plugin
<?php
if (function_exists('matrimonial_display_banner')) {
    matrimonial_display_banner('banner');
}
?>`;

  const apiEndpoints = {
    getActiveAds: `${apiBaseUrl}/wordpress-ads/active`,
    getActiveBySize: `${apiBaseUrl}/wordpress-ads/active?size=banner`,
    trackClick: `${apiBaseUrl}/wordpress-ads/track/click/:adId`,
    trackImpression: `${apiBaseUrl}/wordpress-ads/track/impression`
  };

  return (
    <div className="p-6 space-y-6">
      {/* WordPress Plugin Download Section */}
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Download className="size-5 text-purple-600" />
                WordPress Plugin Package
              </CardTitle>
              <CardDescription className="mt-2">
                Download the complete WordPress plugin to publish on WordPress.org or distribute to users
              </CardDescription>
            </div>
            <Button
              onClick={() => window.open('/wordpress-plugin-admin', '_blank')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Download className="size-4 mr-2" />
              Download Plugin & Guide
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Code className="size-5 text-purple-600" />
                <span className="font-semibold">Complete Plugin</span>
              </div>
              <p className="text-sm text-gray-600">
                Full PHP, JavaScript, CSS package ready for distribution
              </p>
            </div>
            
            <div className="p-4 bg-white rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <ExternalLink className="size-5 text-purple-600" />
                <span className="font-semibold">WordPress.org Ready</span>
              </div>
              <p className="text-sm text-gray-600">
                Publish to reach 40+ million WordPress sites
              </p>
            </div>
            
            <div className="p-4 bg-white rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="size-5 text-purple-600" />
                <span className="font-semibold">Publishing Guide</span>
              </div>
              <p className="text-sm text-gray-600">
                Step-by-step instructions included
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="ads" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="ads">Manage Ads</TabsTrigger>
          <TabsTrigger value="integration">Integration Guide</TabsTrigger>
          <TabsTrigger value="preview">Preview & Test</TabsTrigger>
        </TabsList>

        <TabsContent value="ads" className="space-y-6 mt-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl">WordPress Plugin Ads Management</h1>
              <p className="text-muted-foreground">Manage banners and ads for WordPress plugin</p>
            </div>
            <Button onClick={handleCreateNew}>
              <Plus className="mr-2 h-4 w-4" />
              Create New Ad
            </Button>
          </div>

          {/* Statistics */}
          {stats && (
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm">Total Ads</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl">{stats.totalAds}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm">Active Ads</CardTitle>
                  <Eye className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl">{stats.activeAds}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm">Total Clicks</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl">{stats.totalClicks}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm">Total Impressions</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl">{stats.totalImpressions}</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Ads List */}
          <Card>
            <CardHeader>
              <CardTitle>All Ads</CardTitle>
            </CardHeader>
            <CardContent>
              {loading && ads.length === 0 ? (
                <p>Loading...</p>
              ) : ads.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No ads found. Create your first ad!</p>
              ) : (
                <div className="space-y-4">
                  {ads.map((ad) => (
                    <div key={ad._id} className="flex items-center gap-4 p-4 border rounded-lg">
                      <img src={ad.imageUrl} alt={ad.title} className="w-24 h-24 object-cover rounded" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{ad.title}</h3>
                          <Badge variant={ad.isActive ? "default" : "secondary"}>
                            {ad.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge variant="outline">{ad.size}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Size: {ad.dimensions.width} × {ad.dimensions.height}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Locations: {ad.displayLocations.join(', ')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Clicks: {ad.clickCount} | Impressions: {ad.impressionCount} | CTR: {ad.impressionCount > 0 ? ((ad.clickCount / ad.impressionCount) * 100).toFixed(2) : 0}%
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPreviewAd(ad)}
                        >
                          <Eye className="mr-1 h-4 w-4" />
                          Preview
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(ad._id)}
                        >
                          {ad.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(ad)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(ad._id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integration" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>WordPress Plugin Integration</CardTitle>
              <CardDescription>Follow these steps to integrate ads into your WordPress site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  The WordPress plugin automatically fetches and displays ads from your backend. Make sure your backend is running at <code className="text-sm bg-muted px-1 py-0.5 rounded">{apiBaseUrl}</code>
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg mb-2 flex items-center gap-2">
                    <Code className="h-5 w-5" />
                    Method 1: Using Shortcode
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Add this shortcode anywhere in your WordPress posts, pages, or widgets:
                  </p>
                  <div className="relative">
                    <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                      <code>{shortcodeExample}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(shortcodeExample, 'shortcode')}
                    >
                      {copiedCode === 'shortcode' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Available sizes: banner, sidebar, popup, footer, header, inline, custom
                  </p>
                </div>

                <div>
                  <h3 className="text-lg mb-2 flex items-center gap-2">
                    <Code className="h-5 w-5" />
                    Method 2: Using PHP Function
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Add this code to your theme template files:
                  </p>
                  <div className="relative">
                    <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                      <code>{widgetCode}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(widgetCode, 'widget')}
                    >
                      {copiedCode === 'widget' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg mb-2 flex items-center gap-2">
                    <ExternalLink className="h-5 w-5" />
                    API Endpoints
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Use these endpoints to integrate ads programmatically:
                  </p>
                  <div className="space-y-3">
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm mb-1">Get All Active Ads</p>
                      <code className="text-xs break-all">{apiEndpoints.getActiveAds}</code>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-2"
                        onClick={() => copyToClipboard(apiEndpoints.getActiveAds, 'endpoint1')}
                      >
                        {copiedCode === 'endpoint1' ? (
                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm mb-1">Get Active Ads by Size</p>
                      <code className="text-xs break-all">{apiEndpoints.getActiveBySize}</code>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-2"
                        onClick={() => copyToClipboard(apiEndpoints.getActiveBySize, 'endpoint2')}
                      >
                        {copiedCode === 'endpoint2' ? (
                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm mb-1">Track Click</p>
                      <code className="text-xs break-all">POST {apiEndpoints.trackClick}</code>
                    </div>
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm mb-1">Track Impression</p>
                      <code className="text-xs break-all">POST {apiEndpoints.trackImpression}</code>
                      <p className="text-xs text-muted-foreground mt-1">Body: {`{ "adIds": ["ad_id_1", "ad_id_2"] }`}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg mb-2">Plugin Configuration</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Install and activate the Matrimonial Affiliate Tracker plugin</li>
                    <li>Go to WordPress Admin → Settings → Matrimonial Affiliate</li>
                    <li>Enter your API URL: <code className="bg-muted px-1 py-0.5 rounded">{apiBaseUrl}</code></li>
                    <li>Enter your Affiliate ID</li>
                    <li>Save settings</li>
                    <li>Use the shortcode or widget to display ads</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Ad Preview</CardTitle>
              <CardDescription>Preview how your ads will appear on WordPress sites</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {ads.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No ads available to preview</p>
              ) : (
                <>
                  <div>
                    <Label>Select Ad to Preview</Label>
                    <Select
                      value={previewAd?._id || ''}
                      onValueChange={(value) => {
                        const ad = ads.find(a => a._id === value);
                        setPreviewAd(ad || null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ads.map((ad) => (
                          <SelectItem key={ad._id} value={ad._id}>
                            {ad.title} ({ad.size})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {previewAd && (
                    <div className="space-y-4">
                      <div className="border rounded-lg p-6 bg-background">
                        <p className="text-sm text-muted-foreground mb-4">Preview:</p>
                        <div 
                          className="flex items-center justify-center bg-muted/30 p-4 rounded"
                          style={{ 
                            maxWidth: previewAd.dimensions.width === '100%' ? '100%' : previewAd.dimensions.width,
                            margin: '0 auto'
                          }}
                        >
                          <a
                            href={previewAd.linkUrl}
                            target={previewAd.targetBlank ? '_blank' : '_self'}
                            rel="noopener noreferrer"
                            style={{
                              display: 'block',
                              width: previewAd.dimensions.width,
                              height: previewAd.dimensions.height,
                              position: 'relative',
                              ...(() => {
                                try {
                                  const cssObj: any = {};
                                  if (previewAd.customCSS) {
                                    previewAd.customCSS.split(';').forEach(rule => {
                                      const [prop, value] = rule.split(':').map(s => s.trim());
                                      if (prop && value) {
                                        const camelProp = prop.replace(/-([a-z])/g, g => g[1].toUpperCase());
                                        cssObj[camelProp] = value;
                                      }
                                    });
                                  }
                                  return cssObj;
                                } catch {
                                  return {};
                                }
                              })()
                            }}
                          >
                            <img
                              src={previewAd.imageUrl}
                              alt={previewAd.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                            />
                            <div
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                background: 'rgba(0, 0, 0, 0.6)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '10px'
                              }}
                            >
                              Ad
                            </div>
                          </a>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm">Title:</p>
                          <p className="text-sm text-muted-foreground">{previewAd.title}</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm">Size:</p>
                          <p className="text-sm text-muted-foreground">{previewAd.size}</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm">Dimensions:</p>
                          <p className="text-sm text-muted-foreground">
                            {previewAd.dimensions.width} × {previewAd.dimensions.height}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm">Status:</p>
                          <Badge variant={previewAd.isActive ? "default" : "secondary"}>
                            {previewAd.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm">Performance:</p>
                          <p className="text-sm text-muted-foreground">
                            {previewAd.clickCount} clicks, {previewAd.impressionCount} impressions
                          </p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm">CTR:</p>
                          <p className="text-sm text-muted-foreground">
                            {previewAd.impressionCount > 0 
                              ? ((previewAd.clickCount / previewAd.impressionCount) * 100).toFixed(2) 
                              : 0}%
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAd ? 'Edit Ad' : 'Create New Ad'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="size">Ad Size *</Label>
                <Select
                  value={formData.size}
                  onValueChange={(value) => setFormData({ ...formData, size: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="banner">Banner</SelectItem>
                    <SelectItem value="sidebar">Sidebar</SelectItem>
                    <SelectItem value="popup">Popup</SelectItem>
                    <SelectItem value="footer">Footer</SelectItem>
                    <SelectItem value="header">Header</SelectItem>
                    <SelectItem value="inline">Inline</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="position">Position Order</Label>
                <Input
                  id="position"
                  type="number"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="width">Width</Label>
                <Input
                  id="width"
                  value={formData.width}
                  onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                  placeholder="100% or 300px"
                />
              </div>
              <div>
                <Label htmlFor="height">Height</Label>
                <Input
                  id="height"
                  value={formData.height}
                  onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                  placeholder="auto or 250px"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="imageUrl">Image URL *</Label>
              <Input
                id="imageUrl"
                value={formData.imageUrl}
                onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                placeholder="https://example.com/banner.jpg"
                required
              />
              {formData.imageUrl && (
                <div className="mt-2">
                  <img 
                    src={formData.imageUrl} 
                    alt="Preview" 
                    className="max-w-full h-32 object-contain border rounded"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="linkUrl">Link URL *</Label>
              <Input
                id="linkUrl"
                value={formData.linkUrl}
                onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                placeholder="https://example.com/landing-page"
                required
              />
            </div>

            <div>
              <Label htmlFor="displayLocations">Display Locations</Label>
              <Select
                value={formData.displayLocations[0]}
                onValueChange={(value) => setFormData({ ...formData, displayLocations: [value] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Pages</SelectItem>
                  <SelectItem value="homepage">Homepage</SelectItem>
                  <SelectItem value="blog">Blog</SelectItem>
                  <SelectItem value="profile">Profile</SelectItem>
                  <SelectItem value="search">Search</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="customCSS">Custom CSS (Optional)</Label>
              <Textarea
                id="customCSS"
                value={formData.customCSS}
                onChange={(e) => setFormData({ ...formData, customCSS: e.target.value })}
                placeholder="margin: 20px; border-radius: 10px;"
                rows={3}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="targetBlank"
                checked={formData.targetBlank}
                onCheckedChange={(checked) => setFormData({ ...formData, targetBlank: checked })}
              />
              <Label htmlFor="targetBlank">Open link in new tab</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="trackClicks"
                checked={formData.trackClicks}
                onCheckedChange={(checked) => setFormData({ ...formData, trackClicks: checked })}
              />
              <Label htmlFor="trackClicks">Track clicks</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label htmlFor="isActive">Active</Label>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : editingAd ? 'Update Ad' : 'Create Ad'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}